// Phone/browser push notifications — the client half of
// backend_v2/domain/notifications/push.py. The service worker (src/service-worker.js)
// only exists in a production build, so in `npm start` everything here reports
// "unsupported".
import { apiClient } from "../api/client";

/** "supported" | "ios-install" (iPhone in a Safari tab — push needs the Home Screen app) | "unsupported" */
export function pushSupport() {
  if ("serviceWorker" in navigator && "PushManager" in window && "Notification" in window) {
    return "supported";
  }
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios ? "ios-install" : "unsupported";
}

export function keyBytes(base64url) {
  const padded = base64url + "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

// getRegistration, not .ready — .ready never resolves when no worker is registered.
async function subscription() {
  const reg = await navigator.serviceWorker.getRegistration();
  return { reg, sub: reg ? await reg.pushManager.getSubscription() : null };
}

/** True if this device is currently subscribed. */
export async function isPushOn() {
  if (pushSupport() !== "supported" || Notification.permission !== "granted") return false;
  return Boolean((await subscription()).sub);
}

/**
 * Ask permission and subscribe this device. Must run straight from a click:
 * iOS only shows the permission prompt when it is the first thing awaited.
 * Returns the resulting permission ("granted" | "denied" | "default").
 */
export async function enablePush() {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission;
  if (!(await navigator.serviceWorker.getRegistration())) {
    throw new Error("This browser hasn't finished setting up the app yet. Reload and try again.");
  }
  // A worker registered on this visit may still be installing (downloading the
  // app files); subscribing needs it active. `.ready` is safe here — a
  // registration exists — but capped so a failed install can't hang the button.
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Still setting up the app. Wait a moment and try again.")), 30000)
    ),
  ]);
  const existing = await reg.pushManager.getSubscription();
  const { data } = await apiClient("GET", "/push/config");
  if (!data?.public_key) throw new Error("Phone notifications aren't set up on the server yet.");
  const sub =
    existing ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(data.public_key) }));
  await apiClient("POST", "/push/subscriptions", sub.toJSON());
  return permission;
}

/** Turn this device off: stop the browser subscription and forget it server-side. */
export async function disablePush() {
  const { sub } = await subscription();
  if (!sub) return;
  await apiClient("POST", "/push/subscriptions/remove", { endpoint: sub.endpoint }).catch(() => {});
  await sub.unsubscribe();
}

/**
 * After sign-in: point an existing subscription at whoever is signed in now.
 * Never creates one — turning push off has to stay off. Never throws.
 */
export async function syncPush() {
  try {
    if (pushSupport() !== "supported" || Notification.permission !== "granted") return;
    const { sub } = await subscription();
    if (sub) await apiClient("POST", "/push/subscriptions", sub.toJSON());
  } catch {
    /* best effort (also refused while viewing as someone else) */
  }
}

/**
 * Before sign-out: stop this device getting the signed-out person's
 * notifications. The browser subscription is kept, so the next person who
 * signs in here gets theirs via syncPush. Gives up after 3s so a dead
 * connection never blocks signing out. Never throws.
 */
export async function forgetPushOnSignOut() {
  try {
    if (pushSupport() !== "supported") return;
    const work = (async () => {
      const { sub } = await subscription();
      if (sub) await apiClient("POST", "/push/subscriptions/remove", { endpoint: sub.endpoint });
    })();
    await Promise.race([work, new Promise((resolve) => setTimeout(resolve, 3000))]);
  } catch {
    /* best effort */
  }
}
